import { useState, useEffect, useRef } from "react";

/**
 * Map chainId → DefiLlama chain prefix for the coins API.
 * https://coins.llama.fi/prices/current/{chain}:{address}
 */
const CHAIN_PREFIX: Record<string, string> = {
  "1": "ethereum",
  "10": "optimism",
  "25": "cronos",
  "56": "bsc",
  "100": "gnosis",
  "137": "polygon",
  "146": "sonic",
  "250": "fantom",
  "288": "boba",
  "324": "era",
  "1101": "polygon_zkevm",
  "1284": "moonbeam",
  "1285": "moonriver",
  "1329": "sei",
  "2222": "kava",
  "5000": "mantle",
  "7777777": "zora",
  "8453": "base",
  "34443": "mode",
  "42161": "arbitrum",
  "42220": "celo",
  "43114": "avax",
  "59144": "linea",
  "80094": "berachain",
  "81457": "blast",
  "534352": "scroll",
  "1313161554": "aurora",
  solana: "solana",
  tron: "tron",
};

const EVM_NATIVE_ADDRESSES = new Set([
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
  "0x0000000000000000000000000000000000000000",
]);

const SOLANA_NATIVE = "So11111111111111111111111111111111111111112";
const TRON_NATIVE = "T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb";

const ZERO_ADDR = "0x0000000000000000000000000000000000000000";

// In-memory cache: coinId → { price, timestamp }
const cache = new Map<string, { price: number; ts: number }>();
const CACHE_TTL = 60_000; // 1 min

function getCoinId(chainId: string, address: string): string | null {
  const prefix = CHAIN_PREFIX[chainId];
  if (!prefix) return null;

  // Solana native → use wrapped SOL address for DefiLlama
  if (chainId === "solana" && address === SOLANA_NATIVE) {
    return `solana:${SOLANA_NATIVE}`;
  }
  // Tron native
  if (chainId === "tron" && address === TRON_NATIVE) {
    return `tron:${TRON_NATIVE}`;
  }

  // Only lowercase EVM addresses — base58 (Solana/Tron) is case-sensitive
  const addr = address.startsWith("0x") ? address.toLowerCase() : address;
  // EVM native tokens → use zero address which DefiLlama resolves
  return `${prefix}:${EVM_NATIVE_ADDRESSES.has(addr) ? ZERO_ADDR : addr}`;
}

/**
 * Fetch the current USD price of a token via DefiLlama coins API.
 * Returns null while loading or if the token/chain isn't supported.
 */
export function useTokenPrice(
  chainId: string | undefined,
  address: string | undefined,
): number | null {
  const [price, setPrice] = useState<number | null>(null);
  const abortRef = useRef<AbortController>(undefined);

  useEffect(() => {
    if (!chainId || !address) {
      setPrice(null);
      return;
    }

    const coinId = getCoinId(chainId, address);
    if (!coinId) {
      setPrice(null);
      return;
    }

    // Check cache
    const cached = cache.get(coinId);
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      setPrice(cached.price);
      return;
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    fetch(`https://coins.llama.fi/prices/current/${coinId}`, {
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then((data: { coins?: Record<string, { price?: number }> }) => {
        const p = data.coins?.[coinId]?.price;
        if (p != null) {
          cache.set(coinId, { price: p, ts: Date.now() });
          setPrice(p);
        } else {
          setPrice(null);
        }
      })
      .catch(() => {
        /* abort or network error — ignore */
      });

    return () => ctrl.abort();
  }, [chainId, address]);

  return price;
}
